/**
 * `growth/config` — PURE resolution of the ads configuration.
 *
 * Same three-layer pattern as the monetization kit
 * (`monetization/config.ts`): **code defaults → env → admin UI
 * overrides**. Env and UI share a source: the env provides the default and each override
 * overwrites ONE key live; deleting the override returns control to the env.
 *
 * **OFF by default is sacred**: without `ADS_ENABLED`/`VITE_ADS_ENABLED` the whole layer
 * is inert, `network` is `none` and no SDK is loaded.
 */

import { clampAdsConfigToFinancialPolicy } from "../monetization/ads-policy";
import type { AdContentRating, AdInitOptions, AdsConfig, AdTier, AdTierRegime } from "./types";
import { AD_CONTENT_RATINGS, AD_TIER_REGIMES, AD_TIERS, STRICTEST_AD_TIER_REGIME } from "./types";

/** `none` backend: no-op. It is the DEFAULT and the one that guarantees "no ads, no SDK". */
export const AD_NETWORK_NONE = "none";

/**
 * Backends known to the kit. `none` first on purpose.
 * `fake` is the deterministic provider of the e2e suites (no network or DOM): it is NEVER
 * configured in a real environment.
 */
export const AD_NETWORKS = [AD_NETWORK_NONE, "adsense", "fake"] as const;

/** Admin UI override keys (same source as the env). */
export const AD_OVERRIDE_KEYS = [
  "ads.enabled",
  "ads.network",
  "ads.tier",
  "ads.child_directed",
  "ads.max_content_rating",
  "ads.contextual_only",
  "ads.client_id",
] as const;

export type AdOverrideKey = (typeof AD_OVERRIDE_KEYS)[number];

export function isValidAdOverrideKey(key: string): key is AdOverrideKey {
  return (AD_OVERRIDE_KEYS as readonly string[]).includes(key);
}

/** Code defaults: EVERYTHING off. Enabling ads is an explicit decision. */
export function builtinAdsDefaults(): AdsConfig {
  return {
    enabled: false,
    network: AD_NETWORK_NONE,
    tier: "A",
    childDirected: false,
    maxAdContentRating: "G",
    // Fail-closed: in a money app, "contextual" is default and the only
    // thing the clamp allows through (see `CASHSYNC_AD_REGIME_FLOOR`).
    contextualOnly: true,
    clientId: "",
  };
}

function parseBool(v: string | undefined): boolean | undefined {
  if (v === undefined) {
    return undefined;
  }
  const s = v.trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(s)) {
    return true;
  }
  if (["0", "false", "no", "off"].includes(s)) {
    return false;
  }
  return undefined;
}

function isAdTier(v: string): v is AdTier {
  return (AD_TIERS as readonly string[]).includes(v);
}

function parseTier(v: string | undefined): AdTier | undefined {
  const s = v?.trim().toUpperCase();
  return s && isAdTier(s) ? s : undefined;
}

function isAdContentRating(v: string): v is AdContentRating {
  return (AD_CONTENT_RATINGS as readonly string[]).includes(v);
}

function parseRating(v: string | undefined): AdContentRating | undefined {
  const s = v?.trim().toUpperCase();
  return s && isAdContentRating(s) ? s : undefined;
}

/**
 * The STRICTEST of two ratings. `AD_CONTENT_RATINGS` is ordered from most
 * strict to most lax, so "strictest" = lower index.
 */
export function strictestAdContentRating(a: AdContentRating, b: AdContentRating): AdContentRating {
  return AD_CONTENT_RATINGS.indexOf(a) <= AD_CONTENT_RATINGS.indexOf(b) ? a : b;
}

/**
 * Declared regime of a tier. An unknown tier falls back to the STRICTEST
 * regime (fail-closed): with no declared policy, no advertising is served.
 */
export function adTierRegime(tier: string): AdTierRegime {
  return isAdTier(tier) ? AD_TIER_REGIMES[tier] : STRICTEST_AD_TIER_REGIME;
}

/**
 * Layer 2: env. Both forms of the monorepo are accepted — `ADS_*` (server, and the
 * e2e suites) and `VITE_ADS_*` (web bundle) — with the Vite one winning on the
 * client, which is where the build rules.
 */
export function applyAdsEnv(config: AdsConfig, env: Record<string, string | undefined>): void {
  const enabled = parseBool(env.VITE_ADS_ENABLED) ?? parseBool(env.ADS_ENABLED);
  if (enabled !== undefined) {
    config.enabled = enabled;
  }
  const network = (env.VITE_ADS_NETWORK ?? env.ADS_NETWORK)?.trim().toLowerCase();
  if (network) {
    config.network = network;
  }
  const tier = parseTier(env.VITE_ADS_TIER ?? env.ADS_TIER);
  if (tier) {
    config.tier = tier;
  }
  const child = parseBool(env.VITE_ADS_CHILD_DIRECTED) ?? parseBool(env.ADS_CHILD_DIRECTED);
  if (child !== undefined) {
    config.childDirected = child;
  }
  const rating = parseRating(env.VITE_ADS_MAX_CONTENT_RATING ?? env.ADS_MAX_CONTENT_RATING);
  if (rating) {
    config.maxAdContentRating = rating;
  }
  const contextual = parseBool(env.VITE_ADS_CONTEXTUAL_ONLY) ?? parseBool(env.ADS_CONTEXTUAL_ONLY);
  if (contextual !== undefined) {
    config.contextualOnly = contextual;
  }
  const clientId = (env.VITE_ADS_CLIENT_ID ?? env.ADS_CLIENT_ID)?.trim();
  if (clientId) {
    config.clientId = clientId;
  }
}

/** Layer 3: a back-office override (same semantics as monetization). */
export function applyAdsOverride(config: AdsConfig, key: string, value: unknown): void {
  switch (key) {
    case "ads.enabled": {
      if (typeof value === "boolean") {
        config.enabled = value;
      }
      return;
    }
    case "ads.network": {
      if (typeof value === "string" && value.trim()) {
        config.network = value.trim().toLowerCase();
      }
      return;
    }
    case "ads.tier": {
      const tier = parseTier(typeof value === "string" ? value : undefined);
      if (tier) {
        config.tier = tier;
      }
      return;
    }
    case "ads.child_directed": {
      if (typeof value === "boolean") {
        config.childDirected = value;
      }
      return;
    }
    case "ads.max_content_rating": {
      const rating = parseRating(typeof value === "string" ? value : undefined);
      if (rating) {
        config.maxAdContentRating = rating;
      }
      return;
    }
    case "ads.contextual_only": {
      if (typeof value === "boolean") {
        config.contextualOnly = value;
      }
      return;
    }
    case "ads.client_id": {
      if (typeof value === "string") {
        config.clientId = value.trim();
      }
      return;
    }
    default:
  }
}

/**
 * **Layer 4 — the regime clamp. Classification beats the flag.**
 *
 * Lowers the resolved configuration to the declared FLOOR of its tier
 * (`AD_TIER_REGIMES`) **and to the financial domain floor of this app**
 * (`CASHSYNC_AD_REGIME_FLOOR`). Only hardens: neither env, nor back-office
 * override, nor oversight can relax the regime below the minimum.
 *
 * What it guarantees:
 *   - tiers without ads (`C`, `D`): `enabled=false` and `network=none` ⇒ zero SDK;
 *   - child tiers (`K`): `childDirected=true` ALWAYS;
 *   - rating ceiling per tier, and **`G` whenever there is a child regime**,
 *     wherever it comes from (from tier or from env that HARDENS tier `A`);
 *   - **`contextualOnly=true` ALWAYS** in CashSync: tier `A` by itself
 *     would permit behavioral targeting; domain floor does not.
 *
 * Idempotent and monotonic: applying it twice gives the same, and it never raises.
 *
 * History: canon had C/D clamp from day one, but not the child-tier
 * clamp (`VITE_ADS_CHILD_DIRECTED=false` was enough to serve behavioral
 * ads to a minor) nor the `init()` options clamp. This repo had neither and
 * also carried its own floor —financial— that only lived in placements layer:
 * nothing prevented CONFIG from requesting personalized ads.
 */
export function clampAdsConfigToTier(config: AdsConfig): void {
  const regime = adTierRegime(config.tier);
  if (!regime.adsAllowed) {
    config.enabled = false;
    config.network = AD_NETWORK_NONE;
  }
  if (regime.childDirected) {
    config.childDirected = true;
  }
  // The tier ceiling and the child-regime one are combined: the lower one rules.
  const ceiling = config.childDirected
    ? strictestAdContentRating(regime.maxAdContentRating, "G")
    : regime.maxAdContentRating;
  config.maxAdContentRating = strictestAdContentRating(config.maxAdContentRating, ceiling);
  // And on top of tier floor, that of the money domain.
  clampAdsConfigToFinancialPolicy(config);
}

/** Full resolution: defaults → env → overrides → regime clamp. */
export function resolveAdsConfig(
  env: Record<string, string | undefined>,
  overrides: readonly { key: string; value: unknown }[] = [],
): AdsConfig {
  const config = builtinAdsDefaults();
  applyAdsEnv(config, env);
  for (const o of overrides) {
    applyAdsOverride(config, o.key, o.value);
  }
  clampAdsConfigToTier(config);
  return config;
}

/**
 * Init options HARDENED against the resolved config.
 *
 * The second hole in the same family: `WebAdProvider.init(opts)` took
 * `opts.childDirected` as-is, so a caller could undo the regime
 * just guaranteed. Here the caller can only **harden** (request child-directed,
 * non-personalized ads, or lower rating); relaxing is impossible.
 *
 * Config is re-clamped on a COPY before inspection: thus a hand-built
 * `AdsConfig` (raw JSON, a test, a clone that skipped `resolveAdsConfig`)
 * cannot sneak a relaxed regime past the provider either.
 * Fail-closed at both ends.
 */
export function hardenAdInitOptions(config: AdsConfig, opts: AdInitOptions = {}): AdInitOptions {
  const floor = { ...config };
  clampAdsConfigToTier(floor);
  const childDirected = floor.childDirected || opts.childDirected === true;
  const requested = opts.maxAdContentRating ?? floor.maxAdContentRating;
  let rating = strictestAdContentRating(floor.maxAdContentRating, requested);
  if (childDirected) {
    rating = strictestAdContentRating(rating, "G");
  }
  return {
    childDirected,
    maxAdContentRating: rating,
    // Domain floor: `floor.contextualOnly` is already `true` after clamp, so
    // `opts.contextualOnly === false` has no effect. Written as OR rather
    // than assignment so hardening remains possible if the floor ever stops
    // enforcing it.
    contextualOnly: floor.contextualOnly || opts.contextualOnly === true,
    // It does not affect the policy: passes as-is.
    ...(opts.testDeviceIds ? { testDeviceIds: opts.testDeviceIds } : {}),
  };
}

/**
 * Should this app load the ads kit? Combines the ads config with the verdict
 * of the monetization kit (`shouldShowAds(config, tier)` rules: premium ⇒ 0 ads
 * immediately). Without `enabled` or with network `none`, **zero SDK**.
 */
export function adsAreLive(config: AdsConfig, monetizationSaysAds: boolean): boolean {
  return config.enabled && config.network !== AD_NETWORK_NONE && monetizationSaysAds;
}
